import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { ENTREGA_PANTALLA } from '../../version';

// ═══════════════════════════════════════════════════════════════════
//  "LA APLICACIÓN ESTÁ A MEDIO INSTALAR"
//
//  Al entrar, la pantalla le pregunta al servidor de qué entrega es y lo
//  compara con la suya. Si no coinciden, sale un cartel rojo que lo dice y
//  dice qué falta por hacer.
//
//  POR QUÉ HACE FALTA ESTO
//  ───────────────────────
//  Instalar una entrega son tres pasos: copiar los ficheros, compilar las
//  pantallas (`npm run build`) y reiniciar el servidor (`pm2 restart
//  autocaresrg-api`). Si se salta uno, la aplicación NO se rompe: funciona
//  mal en silencio. La pantalla nueva le manda al servidor viejo un dato que
//  el servidor viejo no conoce, el servidor lo tira, guarda el resto y
//  contesta que todo bien. El usuario ve "Guardado" y no se ha guardado.
//
//  Eso es exactamente lo que pasó con la nota del refuerzo. El código estaba
//  bien desde el primer día; lo que faltaba era el reinicio. Pero no había
//  manera de saberlo desde dentro de la aplicación, y se buscó el fallo
//  donde no estaba.
//
//  CUÁNDO CALLA
//  ────────────
//  Si la petición falla por algo que no sea un 404 —sin red, el servidor
//  caído, el tope de peticiones— NO dice nada. Un cartel rojo que salta
//  porque se fue el wifi un segundo es un cartel que se aprende a ignorar, y
//  entonces ya no sirve para lo que está puesto.
// ═══════════════════════════════════════════════════════════════════

const CLAVE_PLEGADO = 'aviso_version_plegado';

export default function AvisoVersion() {
  const { puede } = useAuth();
  // null = todavía no se sabe, o todo correcto. Un número = la entrega del
  // servidor (0 si es tan viejo que ni tiene la ruta para contestarlo).
  const [servidor, setServidor] = useState(null);
  const [plegado,  setPlegado]  = useState(() => {
    try { return sessionStorage.getItem(CLAVE_PLEGADO) === '1'; }
    catch (e) { return false; }
  });

  useEffect(() => {
    let vivo = true;
    // Se manda la versión de la pantalla para que el servidor lo anote en su
    // registro: así queda constancia del desajuste aunque nadie mire.
    api.get(`/version?pantalla=${ENTREGA_PANTALLA}`)
      .then(({ data }) => {
        if (!vivo) return;
        const n = Number(data && data.entrega) || 0;
        if (n !== ENTREGA_PANTALLA) setServidor(n);
      })
      .catch(err => {
        if (!vivo) return;
        // 404 = este servidor es anterior a la entrega 276 y no tiene la
        // ruta. No es un fallo de red: ES LA RESPUESTA. Un servidor que no
        // sabe decir qué entrega es, es viejo por definición.
        if (err.response && err.response.status === 404) setServidor(0);
        // Lo demás no dice nada concluyente. Callado.
      });
    return () => { vivo = false; };
  }, []);

  if (servidor === null) return null;

  const servidorViejo = servidor === 0 || servidor < ENTREGA_PANTALLA;
  const tecnico = puede && puede('usuarios', 'ver');

  function plegar() {
    setPlegado(true);
    try { sessionStorage.setItem(CLAVE_PLEGADO, '1'); } catch (e) { /* da igual */ }
  }

  // Plegado NO es cerrado: se queda una chapa pequeña, siempre a la vista.
  // Si se pudiera cerrar del todo, se cerraría y volveríamos al punto de
  // partida: la aplicación mal instalada y nadie enterado.
  if (plegado) {
    return (
      <button onClick={() => setPlegado(false)}
        title="La aplicación está a medio instalar — pulsa para ver qué falta"
        style={{ display:'inline-flex', alignItems:'center', gap:6, marginBottom:12,
          background:'#fee2e2', color:'#991b1b', border:'1px solid #fca5a5',
          borderRadius:6, padding:'4px 10px', fontSize:12, fontWeight:700,
          cursor:'pointer' }}>
        ⚠ A medio instalar
      </button>
    );
  }

  return (
    <div style={{ background:'#fee2e2', border:'2px solid #dc2626', borderRadius:8,
      padding:'12px 14px', marginBottom:16, color:'#7f1d1d' }}>

      <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
        <span style={{ fontSize:20, lineHeight:1 }}>⚠</span>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:800, fontSize:15, marginBottom:4 }}>
            LA APLICACIÓN ESTÁ A MEDIO INSTALAR
          </div>

          {servidorViejo ? (
            <div style={{ fontSize:13, lineHeight:1.5 }}>
              Las pantallas son de la <strong>entrega {ENTREGA_PANTALLA}</strong> y el
              servidor {servidor ? <>es de la <strong>entrega {servidor}</strong></>
                                 : <>es <strong>anterior a la 276</strong></>}.
              <br />
              Hasta que se arregle, <strong>puede haber cosas que digan «Guardado»
              y no se guarden</strong>: lo nuevo de las pantallas el servidor
              todavía no lo entiende.
              {tecnico && (
                <div style={{ marginTop:8, background:'#fff', border:'1px solid #fca5a5',
                  borderRadius:6, padding:'8px 10px', fontSize:12.5 }}>
                  Falta reiniciar el servidor:
                  <div style={{ fontFamily:'monospace', fontWeight:700, marginTop:4 }}>
                    pm2 restart autocaresrg-api
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize:13, lineHeight:1.5 }}>
              El servidor es de la <strong>entrega {servidor}</strong> y las pantallas
              son de la <strong>{ENTREGA_PANTALLA}</strong>, más antigua.
              <br />
              Lo más probable es que <strong>el navegador siga con la compilación
              de antes</strong>. Prueba a recargar con <strong>Ctrl + F5</strong>.
              {tecnico && (
                <div style={{ marginTop:8, background:'#fff', border:'1px solid #fca5a5',
                  borderRadius:6, padding:'8px 10px', fontSize:12.5 }}>
                  Si sigue igual, falta compilar las pantallas: en la carpeta
                  <strong> client</strong>,
                  <div style={{ fontFamily:'monospace', fontWeight:700, marginTop:4 }}>
                    npm run build
                  </div>
                </div>
              )}
            </div>
          )}

          {!tecnico && (
            <div style={{ fontSize:12.5, marginTop:6, opacity:.9 }}>
              Avisa a la oficina antes de seguir metiendo datos.
            </div>
          )}
        </div>

        <button onClick={plegar} title="Dejarlo en una chapa pequeña"
          style={{ background:'none', border:'none', color:'#991b1b', cursor:'pointer',
            fontSize:18, lineHeight:1, padding:'0 2px', fontWeight:700 }}>
          ×
        </button>
      </div>
    </div>
  );
}
